import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './i18n'; // Initialize i18n
import HomePage from './pages/HomePage';
import EventsPage from './pages/EventsPage';
import EventDetailPage from './pages/EventDetailPage';
import AuthPage from './pages/AuthPage';
import BuyerDashboard from './pages/BuyerDashboard';
import SellerDashboard from './pages/SellerDashboard';
import AdminDashboard from './pages/AdminDashboard';
import BoothPage from './pages/BoothPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import ExhibitorsPage from './pages/ExhibitorsPage';
import RepLoginPage from './pages/RepLoginPage';
import RepDashboard from './pages/RepDashboard';
import ResetPasswordPage from './pages/ResetPasswordPage';
import CalendarPage from './pages/CalendarPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import SitemapPage from './pages/SitemapPage';
import AdminSetupPage from './pages/AdminSetupPage';
import KnowledgePage from './pages/KnowledgePage';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import SEOHead from './components/SEOHead';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import PushNotificationManager from './components/PushNotificationManager';
import CookieConsent from './components/CookieConsent';
import { Toaster } from './components/ui/sonner';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { ConsentProvider } from './contexts/ConsentContext';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext();

// Layout wrapper that conditionally shows Navigation and Footer
const AppLayout = ({ children, user, showNav = true, showFooter = true }) => {
  return (
    <div className="App min-h-screen bg-background">
      <SEOHead />
      {showNav && <Navigation />}
      {children}
      {showFooter && <Footer />}
      <Toaster position="top-right" />
      <PWAInstallPrompt />
      <PushNotificationManager />
      <CookieConsent />
    </div>
  );
};

// Main app content with routing
const AppContent = ({ user, login, logout, loading }) => {
  const location = useLocation();
  
  // Scroll to top when route changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  
  // Check if current route is a Rep route (separate from main site)
  const isRepRoute = location.pathname.startsWith('/rep-');
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  // Rep routes have their own layout (no main site navigation/footer)
  if (isRepRoute) {
    return (
      <div className="App min-h-screen bg-background">
        <Routes>
          <Route path="/rep-login" element={<RepLoginPage />} />
          <Route path="/rep-dashboard" element={<RepDashboard />} />
        </Routes>
        <Toaster position="top-right" />
      </div>
    );
  }

  // Main site routes with Navigation and Footer
  return (
    <AppLayout user={user}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/events/:eventId" element={<EventDetailPage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/exhibitors" element={<ExhibitorsPage />} />
        <Route path="/booth/:boothId" element={<BoothPage />} />
        <Route path="/auth" element={!user ? <AuthPage /> : <Navigate to="/" />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/forgot-password" element={<ResetPasswordPage />} />
        <Route
          path="/dashboard/buyer"
          element={user && user.role === 'buyer' ? <BuyerDashboard /> : <Navigate to="/auth" />}
        />
        <Route
          path="/dashboard/seller"
          element={user && user.role === 'seller' ? <SellerDashboard /> : <Navigate to="/auth" />}
        />
        <Route
          path="/dashboard/admin"
          element={user && user.role === 'admin' ? <AdminDashboard /> : <Navigate to="/auth" />}
        />
        <Route
          path="/dashboard/rep"
          element={user && user.role === 'rep' ? <RepDashboard /> : <Navigate to="/auth" />}
        />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/knowledge" element={<KnowledgePage />} />
        {/* Legacy routes - redirect to canonical URLs */}
        <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
        <Route path="/terms" element={<Navigate to="/terms-of-service" replace />} />
        <Route path="/sitemap" element={<SitemapPage />} />
        <Route path="/admin/setup" element={<AdminSetupPage />} />
        {/* Legacy rep routes - redirect to unified system */}
        <Route path="/rep-login" element={<Navigate to="/auth" />} />
        <Route path="/rep-dashboard" element={user && user.role === 'rep' ? <RepDashboard /> : <Navigate to="/auth" />} />
      </Routes>
    </AppLayout>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user', error);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  return (
    <ConsentProvider>
      <CurrencyProvider>
        <AuthContext.Provider value={{ user, login, logout }}>
          <BrowserRouter>
            <AppContent user={user} login={login} logout={logout} loading={loading} />
          </BrowserRouter>
        </AuthContext.Provider>
      </CurrencyProvider>
    </ConsentProvider>
  );
}

export default App;
